/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useCallback } from 'react';

import { Button, useAccordionContext } from '@coursera/cds-core';
import { ChevronDownIcon } from '@coursera/cds-icons';
import { useTracker } from '@coursera/event-pulse/react';

import { useEventingProductData } from 'bundles/unified-description-page-common/contexts/EventingContext';

type Props = {
  label: string;
  children: React.ReactNode | React.ReactNode[];
};

const styles = {
  button: css`
    max-width: none;
    box-sizing: border-box;
    display: flex;
    justify-content: space-between;
    text-align: left;
    width: calc(100% + var(--cds-spacing-400));
    margin-left: calc(var(--cds-spacing-200) * -1);
    margin-right: calc(var(--cds-spacing-200) * -1);

    :hover,
    :focus {
      text-decoration: none;
    }

    svg {
      transition: transform 150ms ease-in-out;
      transform: rotate(0deg);
    }

    &[aria-expanded='true'] {
      svg {
        transform: rotate(180deg);
      }
    }
  `,
  label: css`
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-width: 0;
  `,
  labelText: css`
    min-width: 0;
    flex: 1;
  `,
  iconLabel: (expanded?: boolean) => css`
    max-width: 220px;
    min-width: 160px;
    text-align: end;
    opacity: ${expanded ? 1 : 0};
    width: fit-content;

    button:hover &,
    button:focus & {
      opacity: 1;
    }
  `,
};

const HeaderButton = ({ label, children }: Props) => {
  const context = useAccordionContext();
  const track = useTracker();
  const eventingProductData = useEventingProductData();

  const onClick = useCallback<(...args: $TSFixMe[]) => $TSFixMe>(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      const onToggleExpand = context?.onToggleExpanded;

      if (context?.expanded === false) {
        track('expand_accordion', { accordion: { name: 'course_details' }, ...eventingProductData });
      } else if (context?.expanded === true) {
        track('collapse_accordion', { accordion: { name: 'course_details' }, ...eventingProductData });
      }

      if (onToggleExpand) {
        onToggleExpand(event);
      }
    },
    [context, eventingProductData, track]
  );

  return (
    <Button
      ref={context?.headerRef}
      aria-expanded={context?.expanded || false}
      onClick={onClick}
      css={styles.button}
      variant="ghost"
      icon={<ChevronDownIcon size="medium" color="interactive" />}
      iconPosition="after"
    >
      <span css={styles.label}>
        <span css={styles.labelText}>{children}</span>
        <span css={styles.iconLabel(context?.expanded)} aria-hidden>
          {label}
        </span>
      </span>
    </Button>
  );
};

export default HeaderButton;
